import { frameFormatRatio, normalizeFrameFormat } from "@/lib/frame-format-core"

/** Short edge of the previz video buffer (480p, even CSS pixels, dpr=1). */
export const PREVIZ_EXPORT_SHORT_EDGE = 480
/** Long edge of a scene still. Video export does not use this. */
export const SCENE_STILL_LONG_EDGE = 1920

export type PrevizExportSize = { width: number; height: number }

/** Round to nearest even integer ≥ 2 (MediaRecorder prefers even dims). */
function evenRound(n: number): number {
  const v = Math.max(2, Math.round(n))
  return v % 2 === 0 ? v : v + 1
}

/**
 * Fixed drawing-buffer size for path video export (project frame aspect).
 * Short edge is 480. Independent of phone visualViewport so mid-export chrome resize cannot squash frames.
 */
export function previzExportSize(frameFormat?: string | null): PrevizExportSize {
  const ratio = frameFormatRatio(normalizeFrameFormat(frameFormat))
  if (ratio >= 1) {
    const height = PREVIZ_EXPORT_SHORT_EDGE
    const width = evenRound(height * ratio)
    return { width, height }
  }
  const width = PREVIZ_EXPORT_SHORT_EDGE
  const height = evenRound(width / ratio)
  return { width, height }
}

/** Scene poster buffer. Long edge stays 1920; previz video is a separate size. */
export function sceneStillExportSize(frameFormat?: string | null): PrevizExportSize {
  const ratio = frameFormatRatio(normalizeFrameFormat(frameFormat))
  if (ratio >= 1) {
    const width = SCENE_STILL_LONG_EDGE
    const height = evenRound(width / ratio)
    return { width, height }
  }
  const height = SCENE_STILL_LONG_EDGE
  const width = evenRound(height * ratio)
  return { width, height }
}

/**
 * CSS pixel size that fits contentW×contentH inside boxW×boxH (object-fit: contain).
 * Used so export buffer aspect is not stretched across a mismatched stage.
 */
export function containFitSize(contentW: number, contentH: number, boxW: number, boxH: number): PrevizExportSize {
  if (contentW <= 0 || contentH <= 0 || boxW <= 0 || boxH <= 0) {
    return { width: Math.max(0, Math.round(boxW)), height: Math.max(0, Math.round(boxH)) }
  }
  const scale = Math.min(boxW / contentW, boxH / contentH)
  return {
    width: Math.max(1, Math.round(contentW * scale)),
    height: Math.max(1, Math.round(contentH * scale)),
  }
}

/** Share of the stage kept dark around the clear previz frame. */
export const FRAME_VIEWFINDER_PAD_X = 0.12
export const FRAME_VIEWFINDER_PAD_Y = 0.18

/** 16:9 and 21:9 take their width from this side inset. Height follows the aspect. */
const WIDE_FRAME_ASPECT = 16 / 9
const WIDE_FRAME_PAD_X = 0.18

export type FrameViewfinderRect = { x: number; y: number; width: number; height: number }

/**
 * Largest project-aspect rect centered in the stage after padding.
 * Portrait, square, and 4:3: 18% top and bottom, 12% sides.
 * 16:9 and 21:9: width is the stage minus 18% on each side, height = width / aspect.
 */
export function frameViewfinderRect(stageW: number, stageH: number, frameAspect: number): FrameViewfinderRect {
  if (!(stageW > 0) || !(stageH > 0) || !(frameAspect > 0)) {
    return { x: 0, y: 0, width: Math.max(0, stageW), height: Math.max(0, stageH) }
  }
  if (frameAspect >= WIDE_FRAME_ASPECT) {
    let width = Math.max(1, stageW * (1 - 2 * WIDE_FRAME_PAD_X))
    let height = width / frameAspect
    if (height > stageH) {
      height = stageH
      width = height * frameAspect
    }
    return {
      x: (stageW - width) / 2,
      y: (stageH - height) / 2,
      width,
      height,
    }
  }
  const padX = stageW * FRAME_VIEWFINDER_PAD_X
  const padY = stageH * FRAME_VIEWFINDER_PAD_Y
  const innerW = Math.max(1, stageW - padX * 2)
  const innerH = Math.max(1, stageH - padY * 2)
  let width: number
  let height: number
  if (innerW / innerH >= frameAspect) {
    height = innerH
    width = innerH * frameAspect
  } else {
    width = innerW
    height = innerW / frameAspect
  }
  return {
    x: (stageW - width) / 2,
    y: (stageH - height) / 2,
    width,
    height,
  }
}

/** Stage vertical FOV whose center crop matches `frameVFovDeg` inside `rectH`. */
export function frameDisplayVFov(stageH: number, rectH: number, frameVFovDeg: number): number {
  const fraction = stageH > 0 ? rectH / stageH : 1
  const safe = Math.min(1, Math.max(fraction, 0.05))
  const half = (frameVFovDeg * Math.PI) / 360
  return (2 * Math.atan(Math.tan(half) / safe) * 180) / Math.PI
}

/**
 * CSS-pixel buffer of `frameAspect` that covers the stage (object-fit: cover).
 * Phone modals show this buffer full-bleed; the stage clips the overflow.
 */
export function coverBufferSize(frameAspect: number, stageW: number, stageH: number): PrevizExportSize {
  if (!(frameAspect > 0) || stageW < 1 || stageH < 1) {
    return { width: Math.max(1, Math.round(stageW)), height: Math.max(1, Math.round(stageH)) }
  }
  if (stageW / stageH > frameAspect) {
    return { width: Math.round(stageW), height: Math.max(1, Math.round(stageW / frameAspect)) }
  }
  return { width: Math.max(1, Math.round(stageH * frameAspect)), height: Math.round(stageH) }
}
